// features/forms/page/forms-list/forms-list.ts
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Sort } from '@angular/material/sort';

import { MatDialog, MatDialogModule } from '@angular/material/dialog';

import { UiMoleculeMatTableComponent } from '@shared/components/molecules/ui-molecule-mat-table/ui-molecule-mat-table.component';
import { TableColumn } from '@shared/components/molecules/ui-molecule-mat-table/interfaces/table-column';
import { TableAction } from '@shared/components/molecules/ui-molecule-mat-table/interfaces/table-action';
import { ActionEvent } from '@shared/components/molecules/ui-molecule-mat-table/interfaces/action-event';
import { UiAtomButtonComponent } from '@shared/components/atoms/ui-atom-button/ui-atom-button.component';
import { UiMoleculeDialogDeleteWithConfirmComponent } from '@shared/components/molecules/ui-molecule-dialog-delete-with-confirm/ui-molecule-dialog-delete-with-confirm.component';
import { UiOrganismMainNavComponent } from '@shared/components/organisms/ui-organism-main-nav/ui-organism-main-nav.component';
import { UiAtomSpacerComponent } from '@shared/components/atoms/ui-atom-spacer/ui-atom-spacer.component';
import { UiAtomTextComponent } from '@shared/components/atoms/ui-atom-text/ui-atom-text.component';
import {
  FormUpsertDialogComponent,
  FormDialogResult,
} from '@shared/components/molecules/ui-molecule-dialog-form-upsert/ui-molecule-dialog-form-upsert.component';
import {
  FormResponseDialogComponent,
  FormResponseData,
} from '@shared/components/molecules/ui-molecule-dialog-form-response/ui-molecule-dialog-form-response.component';

import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { Form } from '@core/entities/models/form.model';
import { FormService, FormListParams } from '@core/services/use-cases/form.service';
import { AppRoutes } from '@core/constants/app.routes';
import { SessionService } from '@core/services/use-cases/session.service';
import { UserRole, hasPermission } from '@core/entities/models/role.model';
import { DialogFormDashboardService } from '@shared/services/dialog-form-dashboard.service';
import { SnackbarService } from '@shared/services/snackbar.service';

@Component({
  selector: 'app-forms-list',
  standalone: true,
  imports: [
    CommonModule,
    UiMoleculeMatTableComponent,
    UiAtomButtonComponent,
    MatDialogModule,
    TranslatePipe,
    UiOrganismMainNavComponent,
    UiAtomSpacerComponent,
    UiAtomTextComponent,
    FormUpsertDialogComponent,
  ],
  templateUrl: './forms-list.html',
  styleUrl: './forms-list.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormsList implements OnInit {
  // ---------------------------------------------------------------------------
  // Estado de la tabla
  // ---------------------------------------------------------------------------

  /** Datos que alimentan la tabla (mock desde localStorage por ahora) */
  protected tableData: Form[] = [];

  /** Definición de columnas para ui-molecule-mat-table */
  protected tableColumns: TableColumn[] = [];

  /** Acciones de la tabla (iconos + evento que emite ui-molecule-mat-table) */
  protected tableActions: TableAction[] = [];

  /** Estado de paginación (modo servidor) */
  protected pageIndex = 0;
  protected pageSize = 10;
  protected totalItems = 0;
  protected isLoading = false;

  // Ordenación servidor
  protected formSortBy: FormListParams['sortBy'] = undefined;
  protected formSortOrder: 'ASC' | 'DESC' | undefined = undefined;
  protected formFilter: string | undefined = undefined;

  /** Permisos del usuario actual */
  protected canCreateForms = false;
  protected canEditForms = false;
  protected canDeleteForms = false;

  // ---------------------------------------------------------------------------
  // Constructor e inyección de dependencias
  // ---------------------------------------------------------------------------

  constructor(
    private readonly formService: FormService,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly cdr: ChangeDetectorRef,
    private readonly sessionService: SessionService,
    private readonly translateService: TranslateService,
    private readonly dashboardDialog: DialogFormDashboardService,
    private readonly snackbar: SnackbarService,
  ) {}

  // ---------------------------------------------------------------------------
  // Ciclo de vida
  // ---------------------------------------------------------------------------

  ngOnInit(): void {
    this.loadPermissions();
    this.buildTableColumns();
    this.loadTable();
  }

  /**
   * Carga los permisos del usuario actual.
   */
  private loadPermissions(): void {
    const currentUser = this.sessionService.getCurrentUser();
    if (!currentUser) return;

    const role = currentUser.role;
    this.canCreateForms = hasPermission(role, 'canCreateForms');
    this.canEditForms = hasPermission(role, 'canEditForms');
    this.canDeleteForms = hasPermission(role, 'canDeleteForms');

    // Actualizar acciones de la tabla según permisos
    this.updateTableActions();
  }

  /**
   * Actualiza las acciones disponibles en la tabla según permisos y estado del formulario.
   * Según OpenAPI:
   * - DRAFT: puede editar, eliminar, publicar
   * - FINAL + publication != null: sin acciones (formulario publicado)
   */
  private updateTableActions(): void {
    const actions: TableAction[] = [];

    // Copy Link: Solo si status = FINAL (formulario publicado)
    actions.push({
      icon: 'link',
      tooltip: 'Copy public link',
      tooltipKey: 'forms.list.table.actions.copyLink',
      event: 'copyLink',
      showIf: (form: Form) => form.status === 'FINAL',
    });

    // Dashboard: Solo SUPER_ADMIN, sobre forms publicados (FINAL + publication)
    actions.push({
      icon: 'space_dashboard',
      tooltip: 'View dashboard',
      tooltipKey: 'forms.list.table.actions.dashboard',
      event: 'dashboard',
      showIf: (form: Form) =>
        form.status === 'FINAL' &&
        !!form.publication &&
        this.sessionService.getCurrentUser()?.role === UserRole.ADMIN,
      disabledIf: (form: Form) => {
        // El endpoint global no devuelve `submissions.total`. Solo podemos
        // deshabilitar de forma fiable cuando no hay publicación, lo cual ya
        // está cubierto por el showIf de arriba — por lo que `disabledIf` aquí
        // es prácticamente un no-op. En caso de que llegara el contador en el
        // futuro, esta lógica se aprovecharía automáticamente.
        if (!form.publication) return true;
        const total = (form.publication as any).submissions?.total;
        return total !== undefined && total === 0;
      },
    });

    // Publicar: Solo si status = DRAFT (formulario en borrador)
    actions.push({
      icon: 'rocket',
      tooltip: 'Publish form',
      tooltipKey: 'forms.list.table.actions.publish',
      event: 'publish',
      showIf: (form: Form) => form.status === 'DRAFT',
    });

    // Edit: Solo si status = DRAFT y tiene permiso
    if (this.canEditForms) {
      actions.push({
        icon: 'edit',
        tooltip: 'Edit form',
        tooltipKey: 'forms.list.table.actions.edit',
        event: 'edit',
        showIf: (form: Form) => form.status === 'DRAFT',
      });
    }

    // Delete: Solo si status = DRAFT y tiene permiso
    if (this.canDeleteForms) {
      actions.push({
        icon: 'delete',
        tooltip: 'Delete form',
        tooltipKey: 'forms.list.table.actions.delete',
        event: 'delete',
        showIf: (form: Form) => form.status === 'DRAFT',
      });
    }

    this.tableActions = actions;
  }

  // ---------------------------------------------------------------------------
  // Configuración de columnas
  // ---------------------------------------------------------------------------

  /**
   * Construye las columnas de la tabla según OpenAPI:
   * - name: campo directo del formulario
   *   (createdAt / updatedAt ocultas — ver marca HIDDEN-COLUMNS)
   * - publishedAt: publication.publishedAt o '-' si DRAFT
   * - countries: publication.assignments o '-' si DRAFT
   */
  private buildTableColumns(): void {
    this.tableColumns = [
      {
        key: 'name',
        title: 'Form name',
        titleKey: 'forms.list.table.columns.name',
        type: 'text',
        sortable: true,
        filterable: true,
        filterType: 'input',
        placeholder: 'Filter by form name',
        placeholderKey: 'forms.list.table.filters.name',
      },
      // HIDDEN-COLUMNS: 'createdAt' y 'updatedAt' ocultas temporalmente.
      // Para volver a mostrarlas, descomentar el bloque siguiente (no requiere más cambios:
      // las claves i18n y el mapeo de ordenación en onTableSortChange siguen vigentes).
      // {
      //   key: 'createdAt',
      //   title: 'Created at',
      //   titleKey: 'forms.list.table.columns.createdAt',
      //   type: 'date',
      //   width: '140px',
      //   sortable: true,
      //   filterable: false,
      // },
      // {
      //   key: 'updatedAt',
      //   title: 'Last change',
      //   titleKey: 'forms.list.table.columns.updatedAt',
      //   type: 'date',
      //   width: '140px',
      //   sortable: true,
      //   filterable: false,
      // },
      {
        key: 'publishedAt',
        title: 'Published at',
        titleKey: 'forms.list.table.columns.publishedAt',
        type: 'custom',
        width: '140px',
        filterable: false,
        getValue: (row: Form) =>
          // Solo mostrar fecha si status=FINAL y publication existe
          row.status === 'FINAL' && row.publication?.publishedAt
            ? new Date(row.publication.publishedAt).toLocaleDateString()
            : '-',
      },
      {
        key: 'countries',
        title: 'Countries',
        titleKey: 'forms.list.table.columns.countries',
        type: 'custom',
        width: '100px',
        sortable: true,
        filterable: false,
        // Solo mostrar assignments si status=FINAL y publication existe
        getValue: (row: Form) =>
          row.status === 'FINAL' && row.publication?.assignments != null
            ? row.publication.assignments.toString()
            : '-',
      },
    ];
  }

  // ---------------------------------------------------------------------------
  // Carga / refresco de datos
  // ---------------------------------------------------------------------------

  private loadTable(): void {
    this.isLoading = true;
    const params: FormListParams = {
      offset: this.pageIndex * this.pageSize,
      limit: this.pageSize,
    };

    if (this.formFilter && this.formFilter.length >= 3) {
      params.filter = this.formFilter;
    }
    if (this.formSortBy) {
      params.sortBy = this.formSortBy;
    }
    if (this.formSortOrder) {
      params.sortOrder = this.formSortOrder;
    }

    this.formService.getAll(params).subscribe({
      next: (response) => {
        this.tableData = response.items;
        this.totalItems = response.total;
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.snackbar.notifyHttpError(err);
        this.tableData = [];
        this.isLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  // ---------------------------------------------------------------------------
  // Handlers de eventos de la tabla
  // ---------------------------------------------------------------------------

  protected onTableAction(actionEvent: ActionEvent): void {
    const { event, rowData } = actionEvent;
    const row = rowData as Form;

    switch (event) {
      case 'copyLink':
        this.onCopyLink(row);
        break;

      case 'edit':
        this.onEdit(row);
        break;

      case 'publish':
        this.onPublish(row);
        break;

      case 'delete':
        this.onDelete(row);
        break;

      case 'dashboard':
        this.onDashboard(row);
        break;

      default:
        break;
    }
  }

  private onDashboard(form: Form): void {
    this.formService.getById(form.id).subscribe({
      next: (fullForm) => {
        this.formService.getAllSubmittedAnswersForForm(form.id).subscribe({
          next: (responses) => {
            this.dashboardDialog.open({
              formName: fullForm.name,
              schema: fullForm.schema ?? {},
              responses,
              scopeLabel: this.translateService.instant('forms.dashboard.scope.global'),
            });
          },
          error: () => {
            this.snackbar.open(this.translateService.instant('forms.dashboard.empty'));
          },
        });
      },
      error: (err) => this.snackbar.notifyHttpError(err),
    });
  }

  protected onTableRowClick(row: Form): void {
    const dialogData: FormResponseData = {
      title: this.translateService.instant('forms.form.viewTitle'),
      formName: row.name,
      schema: row.schema || {},
      mode: 'view',
      formId: row.id,
    };

    this.dialog.open(FormResponseDialogComponent, {
      data: dialogData,
      panelClass: ['dialog-xl', 'dialog-safe-area', 'dialog-flex'],
      width: '90vw',
      maxWidth: '1200px',
      height: '90vh',
      disableClose: false,
    });
  }

  protected onTablePageChange(event: { pageIndex: number; pageSize: number }): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadTable();
  }

  protected onGlobalSearch(searchTerm: string): void {
    this.formFilter = searchTerm;
    if (searchTerm && searchTerm.length >= 3) {
      this.pageIndex = 0;
      this.loadTable();
    } else if (!searchTerm || searchTerm.length === 0) {
      this.formFilter = undefined;
      this.pageIndex = 0;
      this.loadTable();
    }
  }

  protected onTableFilterChange(filters: Record<string, string>): void {
    const filterValue = Object.values(filters).find((v) => v && v.length >= 3);
    if (filterValue) {
      this.formFilter = filterValue;
      this.pageIndex = 0;
      this.loadTable();
    } else if (Object.values(filters).every((v) => !v || v.length === 0)) {
      this.formFilter = undefined;
      this.pageIndex = 0;
      this.loadTable();
    }
  }

  protected onTableSortChange(sort: Sort): void {
    const columnToApiField: Record<string, FormListParams['sortBy']> = {
      name: 'name',
      createdAt: 'createdAt',
      updatedAt: 'updatedAt',
      countries: 'publication.assignments',
    };

    if (sort.direction) {
      this.formSortBy = columnToApiField[sort.active];
      this.formSortOrder = sort.direction.toUpperCase() as 'ASC' | 'DESC';
    } else {
      this.formSortBy = undefined;
      this.formSortOrder = undefined;
    }

    this.pageIndex = 0;
    this.loadTable();
  }

  // ---------------------------------------------------------------------------
  // Acciones de alto nivel
  // ---------------------------------------------------------------------------

  /**
   * Publica un formulario (cambia status de DRAFT a FINAL).
   * Muestra diálogo de confirmación antes de publicar.
   */
  private onPublish(form: Form): void {
    const dialogRef = this.dialog.open(UiMoleculeDialogDeleteWithConfirmComponent, {
      disableClose: true,
      autoFocus: false,
      panelClass: ['dialog-sm', 'dialog-safe-area'],
      data: {
        title: this.translateService.instant('forms.list.dialogs.publish.title'),
        message: this.translateService.instant('forms.list.dialogs.publish.message'),
        confirmText: this.translateService.instant('forms.list.dialogs.publish.confirm'),
        itemName: form.name,
      },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;

      this.formService.finalize(form.id).subscribe({
        next: () => {
          // Recargar la tabla para obtener el estado actualizado
          this.loadTable();
        },
        error: (err) => this.snackbar.notifyHttpError(err),
      });
    });
  }

  private onEdit(row: Form): void {
    this.formService.getById(row.id).subscribe({
      next: (fullForm) => {
        this.openEditFormDialog(fullForm);
      },
      error: (err) => this.snackbar.notifyHttpError(err),
    });
  }

  private openEditFormDialog(form: Form): void {
    const dialogRef = this.dialog.open(FormUpsertDialogComponent, {
      width: '90vw',
      maxWidth: '1400px',
      height: '90vh',
      panelClass: ['dialog-fullscreen', 'dialog-safe-area', 'dialog-flex'],
      data: {
        title: this.translateService.instant('forms.form.editTitle'),
        mode: 'edit',
        form: form,
        context: 'global',
      },
    });

    dialogRef.afterClosed().subscribe((result: FormDialogResult | undefined) => {
      if (result && result.mode === 'edit' && result.formId) {
        this.handleFormUpdate(result.formId, result.payload);
      }
    });
  }

  private handleFormUpdate(formId: string, payload: any): void {
    this.formService.update(formId, payload).subscribe({
      next: () => {
        this.loadTable();
      },
      error: (err) => this.snackbar.notifyHttpError(err),
    });
  }

  private onDelete(row: Form): void {
    const dialogRef = this.dialog.open(UiMoleculeDialogDeleteWithConfirmComponent, {
      disableClose: true,
      autoFocus: false,
      panelClass: ['dialog-sm', 'dialog-safe-area'],
      data: {
        itemName: row.name,
      },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;

      this.formService.delete(row.id).subscribe({
        next: () => {
          this.loadTable();
        },
        error: (err) => this.snackbar.notifyHttpError(err),
      });
    });
  }

  /**
   * Copia el enlace público del formulario al portapapeles.
   * El enlace usa el token de publicación si está disponible, o el formId como fallback.
   * En GitHub Pages develop, incluye el baseHref /jrs-forms-web/
   */
  private onCopyLink(form: Form): void {
    const baseUrl = window.location.origin;
    const currentPath = window.location.pathname;

    // Usar token si está disponible, sino usar formId como fallback temporal
    const identifier = form.publication?.token || form.id;

    // Detectar si estamos en GitHub Pages develop con subdirectorio /jrs-forms-web/
    // Solo añadir el prefijo si:
    // 1. Estamos en github.io (GitHub Pages)
    // 2. La URL contiene /jrs-forms-web/ (entorno develop)
    const isGitHubPages = window.location.hostname.includes('github.io');
    const isGitHubPagesDev = isGitHubPages && currentPath.startsWith('/jrs-forms-web/');
    const basePath = isGitHubPagesDev ? '/jrs-forms-web' : '';
    const publicUrl = `${baseUrl}${basePath}/#/public/form/${identifier}`;

    if (!form.publication?.token) {
      console.warn('⚠️ Using formId as fallback (token not available yet)');
    }

    console.log('🔍 Copy-link debug:', {
      baseUrl,
      currentPath,
      isGitHubPagesDev,
      basePath,
      publicUrl,
    });

    navigator.clipboard.writeText(publicUrl).then(
      () => {
        console.log('✅ Link copied to clipboard:', publicUrl);
        alert(this.translateService.instant('forms.list.messages.linkCopied'));
      },
      (err) => {
        console.error('❌ Error copying link:', err);
        alert(this.translateService.instant('forms.list.messages.linkCopyError'));
      },
    );
  }

  protected goToCreate(): void {
    this.openCreateFormDialog();
  }

  private openCreateFormDialog(): void {
    const dialogRef = this.dialog.open(FormUpsertDialogComponent, {
      width: '90vw',
      maxWidth: '1400px',
      height: '90vh',
      panelClass: ['dialog-fullscreen', 'dialog-safe-area', 'dialog-flex'],
      data: {
        title: this.translateService.instant('forms.form.createTitle'),
        mode: 'create',
        context: 'global',
      },
    });

    dialogRef.afterClosed().subscribe((result: FormDialogResult | undefined) => {
      if (result && result.mode === 'create') {
        this.handleFormCreate(result.payload);
      }
    });
  }

  private handleFormCreate(payload: any): void {
    this.formService.create(payload).subscribe({
      next: () => {
        this.loadTable();
      },
      error: (err) => this.snackbar.notifyHttpError(err),
    });
  }
}
